import API_BASE from '../../utils/api';
import { formatLocalDate } from '../../utils/date';
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Sidebar from "../../components/Sidebar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ProfileDropdown from "../../components/ProfileDropdown";

const humanize = (key) => key.replace(/([A-Z])/g, " $1").trim();
const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const AdminReports = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reportType, setReportType] = useState("attendance");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(1)), // First day of current month
    endDate: new Date(),
  });
  const [selectedClass, setSelectedClass] = useState("");
  const [classes, setClasses] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE}/classes`);
      setClasses(response.data);
      if (response.data.length > 0) {
        setSelectedClass(response.data[0]._id);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: formatLocalDate(dateRange.startDate),
        endDate: formatLocalDate(dateRange.endDate),
      };
      if (selectedClass && reportType !== "leaves") params.classId = selectedClass;

      const res = await axios.get(`${API_BASE}/reports/${reportType}`, { params });
      setReportData(res.data);
    } catch (error) {
      console.error("Error generating report:", error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const reportFilename = (ext) => `${reportType}_report_${new Date().toISOString().split("T")[0]}.${ext}`;

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    if (!reportData) return;
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: "application/json" });
    downloadBlob(blob, reportFilename("json"));
  };

  const exportXLS = () => {
    if (!reportData) return;
    const summaryRows = Object.entries(reportData.summary)
      .map(([k, v]) => `<tr><td>${escapeHtml(humanize(k))}</td><td>${escapeHtml(v)}</td></tr>`)
      .join("");
    const dataHeaders = reportData.data ? Object.keys(reportData.data[0]) : [];
    const dataRows = reportData.data
      ? reportData.data
          .map((row) => `<tr>${Object.values(row).map((v) => `<td>${escapeHtml(v)}</td>`).join("")}</tr>`)
          .join("")
      : "";
    const html = `<html><head><meta charset="UTF-8"></head><body>
      <h2>${escapeHtml(reportData.title)}</h2>
      <p>Period: ${escapeHtml(reportData.period)}</p>
      <table border="1"><thead><tr><th>Metric</th><th>Value</th></tr></thead><tbody>${summaryRows}</tbody></table>
      <br/>
      ${dataHeaders.length ? `<table border="1"><thead><tr>${dataHeaders.map((h) => `<th>${escapeHtml(humanize(h))}</th>`).join("")}</tr></thead><tbody>${dataRows}</tbody></table>` : ""}
    </body></html>`;
    const blob = new Blob(["﻿", html], { type: "application/vnd.ms-excel" });
    downloadBlob(blob, reportFilename("xls"));
  };

  const exportPDF = () => {
    if (!reportData) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(reportData.title, 14, 18);
    doc.setFontSize(10);
    doc.text(`Period: ${reportData.period}`, 14, 25);

    autoTable(doc, {
      startY: 32,
      head: [["Metric", "Value"]],
      body: Object.entries(reportData.summary).map(([k, v]) => [humanize(k), String(v)]),
    });

    if (reportData.data && reportData.data.length > 0) {
      const headers = Object.keys(reportData.data[0]).map(humanize);
      const body = reportData.data.map((row) => Object.values(row).map((v) => String(v)));
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [headers],
        body,
      });
    }

    doc.save(reportFilename("pdf"));
  };

  const handleExport = (format) => {
    setShowExportMenu(false);
    if (format === "json") exportJSON();
    else if (format === "xls") exportXLS();
    else if (format === "pdf") exportPDF();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 bg-gray-100 min-h-screen">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Reports</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {/* Report Filters */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Generate Report</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Type
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="attendance">Attendance Report</option>
                  <option value="fees">Fee Collection Report</option>
                  <option value="leaves">Leave Report</option>
                  <option value="academic">Academic Report</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={(date) =>
                    setDateRange({ ...dateRange, startDate: date })
                  }
                  className="w-full border rounded-md px-3 py-2"
                  dateFormat="yyyy-MM-dd"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <DatePicker
                  selected={dateRange.endDate}
                  onChange={(date) =>
                    setDateRange({ ...dateRange, endDate: date })
                  }
                  className="w-full border rounded-md px-3 py-2"
                  dateFormat="yyyy-MM-dd"
                  minDate={dateRange.startDate}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Class (Optional)
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">All Classes</option>
                  {classes.map((cls) => (
                    <option key={cls._id} value={cls._id}>
                      {cls.className} - {cls.section}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex space-x-3">
              <button
                onClick={generateReport}
                disabled={loading}
                className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 disabled:bg-indigo-300"
              >
                {loading ? "Generating..." : "Generate Report"}
              </button>
              {reportData && (
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu((s) => !s)}
                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
                  >
                    Export Report ▾
                  </button>
                  {showExportMenu && (
                    <div className="absolute left-0 mt-1 w-36 bg-white border rounded-lg shadow-lg z-20 overflow-hidden">
                      <button onClick={() => handleExport("xls")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export XLS</button>
                      <button onClick={() => handleExport("json")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export JSON</button>
                      <button onClick={() => handleExport("pdf")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export PDF</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Report Results */}
          {reportData && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 bg-indigo-600 text-white">
                <h2 className="text-lg font-semibold">{reportData.title}</h2>
                <p className="text-sm text-indigo-100">
                  Period: {reportData.period}
                </p>
              </div>

              <div className="p-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {Object.entries(reportData.summary).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 capitalize">
                        {key.replace(/([A-Z])/g, " $1").trim()}
                      </p>
                      <p className="text-xl font-bold mt-1">
                        {typeof value === "number" && key.includes("amount")
                          ? `₹${value.toLocaleString()}`
                          : value}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Data Table */}
                {reportData.data && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(reportData.data[0]).map((key) => (
                            <th
                              key={key}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              {key.replace(/([A-Z])/g, " $1").trim()}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportData.data.map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value, i) => (
                              <td
                                key={i}
                                className="px-6 py-4 whitespace-nowrap"
                              >
                                {typeof value === "number" &&
                                value.toString().includes("amount")
                                  ? `₹${value.toLocaleString()}`
                                  : value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
